import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VpnKey as VpnKeyIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAdminUser } from '../utils/auth';
import {
  DataTable,
  PageHeader,
  ErrorState,
  type Column,
  type SortDirection,
} from '../components/common';

type Customer = {
  id: string;
  customer_tag: string;
  name: string;
  active: boolean;
  user_type: string;
  created_at: string;
  updated_at: string;
  config: any;
  stripe_customer_id?: string;
  email?: string;
  api_keys_count?: number;
  total_api_calls?: number;
  total_tokens?: number;
  avg_monthly_tokens?: number;
};

type CustomerDetails = Customer & {
  firebase_users?: Array<{
    email: string;
    display_name?: string;
    is_admin: boolean;
    active: boolean;
    last_login_at?: string;
  }>;
  api_keys?: Array<{
    id: number;
    name?: string;
    active: boolean;
    created_at?: string;
    last_used_at?: string;
    max_tokens_monthly: number;
    usage_count?: number;
  }>;
};

type UsageDetail = {
  summary: {
    total_tokens: number;
    function_tokens: number;
    chain_tokens: number;
    total_runs: number;
    function_runs: number;
    chain_runs: number;
    functions_created: number;
    chains_created: number;
  };
  functions: Array<{
    id: string;
    name: string;
    total_runs: number;
    total_tokens: number;
    avg_tokens_per_run: number;
    success_rate: number;
    last_run?: string;
  }>;
  chains: Array<{
    id: string;
    name: string;
    total_executions: number;
    total_tokens: number;
    avg_tokens_per_execution: number;
    last_execution?: string;
  }>;
};

export const AdminCustomers: React.FC = () => {
  const { user, permissions } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({ open: false, message: '', severity: 'success' });
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  
  // Sorting
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');
  
  // Customer details dialog
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Edit customer dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState<Customer | null>(null);
  const [editFormData, setEditFormData] = useState<{
    user_type: string;
    active: boolean;
    stripe_customer_id?: string;
  }>({
    user_type: 'customer',
    active: true,
  });

  // Usage detail for the customer details dialog
  const [usageDetail, setUsageDetail] = useState<UsageDetail | null>(null);
  const [loadingUsage, setLoadingUsage] = useState(false);

  // Check if user is admin on mount
  useEffect(() => {
    if (!isAdminUser(permissions?.userType)) {
      navigate('/dashboard');
    }
  }, [permissions, navigate]);

  const loadCustomers = async () => {
    setLoading(true);
    setError(null);
    try {
      const idToken = await user?.getIdToken();
      const params = new URLSearchParams({
        page: String(page + 1),
        page_size: String(rowsPerPage),
        search: search,
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      
      const res = await fetch(`/api/v2/admin/customers?${params}`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      
      if (!res.ok) throw new Error(await res.text());
      
      const data = await res.json();
      setCustomers(data.data || []);
      setTotalCount(data.total || 0);
    } catch (e: any) {
      setError(e?.message || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && isAdminUser(permissions?.userType)) {
      loadCustomers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, page, rowsPerPage, sortBy, sortDir]);

  const handleSearch = () => {
    setPage(0);
    loadCustomers();
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
    setPage(0);
  };

  const handleViewDetails = async (customer: Customer) => {
    setSelectedCustomer(null);
    setUsageDetail(null);
    setDetailsOpen(true);
    setLoadingDetails(true);
    setLoadingUsage(true);
    
    const idToken = await user?.getIdToken();
    const headers = { Authorization: `Bearer ${idToken}` };

    // Fetch customer details and usage detail in parallel
    try {
      const [detailRes, usageRes] = await Promise.all([
        fetch(`/api/v2/admin/customers/${customer.id}`, { headers }),
        fetch(`/api/v2/admin/customers/${customer.id}/usage-detail`, { headers }),
      ]);
      
      if (!detailRes.ok) throw new Error(await detailRes.text());
      const detailData = await detailRes.json();
      setSelectedCustomer(detailData.data);

      if (usageRes.ok) {
        const usageData = await usageRes.json();
        setUsageDetail(usageData.data);
      }
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.message || 'Failed to load customer details', severity: 'error' });
      setDetailsOpen(false);
    } finally {
      setLoadingDetails(false);
      setLoadingUsage(false);
    }
  };

  const handleEditCustomer = (customer: Customer) => {
    setEditCustomer(customer);
    setEditFormData({
      user_type: customer.user_type,
      active: customer.active,
      stripe_customer_id: customer.stripe_customer_id,
    });
    setEditOpen(true);
  };

  const handleUpdateCustomer = async () => {
    if (!editCustomer) return;
    
    try {
      const idToken = await user?.getIdToken();
      const res = await fetch(`/api/v2/admin/customers/${editCustomer.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}` 
        },
        body: JSON.stringify(editFormData),
      });
      
      if (!res.ok) throw new Error(await res.text());
      
      setEditOpen(false);
      await loadCustomers();
      setSnackbar({ open: true, message: 'Customer updated successfully', severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.message || 'Failed to update customer', severity: 'error' });
    }
  };

  const handleViewApiKeys = (customer: Customer) => {
    navigate(`/dashboard/admin/customers/${customer.id}/api-keys`);
  };

  const handleViewHistory = (customer: Customer) => {
    navigate(`/dashboard/function-runs?customer_id=${customer.id}&customer_tag=${customer.customer_tag}`);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const formatConfig = (config: any) => {
    if (!config) return '-';
    if (typeof config === 'string') {
      try {
        config = JSON.parse(config);
      } catch {
        return config;
      }
    }
    return JSON.stringify(config, null, 2);
  };

  const columns: Column<Customer>[] = [
    {
      id: 'customer_tag',
      label: 'Customer Tag',
      sortable: true,
    },
    {
      id: 'name',
      label: 'Name',
      sortable: true,
    },
    {
      id: 'email',
      label: 'Email',
      sortable: false,
      render: (value) => value || '-',
    },
    {
      id: 'user_type',
      label: 'User Type',
      sortable: true,
      render: (value) => (
        <Chip 
          label={value} 
          color={value === 'admin' ? 'primary' : 'default'}
          size="small"
        />
      ),
    },
    {
      id: 'active',
      label: 'Active',
      sortable: true,
      render: (value) => (
        <Chip 
          label={value ? 'Active' : 'Inactive'} 
          color={value ? 'success' : 'error'}
          size="small"
        />
      ),
    },
    {
      id: 'stripe_customer_id',
      label: 'Stripe Customer',
      sortable: false,
      render: (value) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
          {value || '-'}
        </Typography>
      ),
    },
    {
      id: 'total_tokens',
      label: 'Total Tokens',
      sortable: false,
      numeric: true,
      render: (value) => value ? value.toLocaleString() : '0',
    },
    {
      id: 'avg_monthly_tokens',
      label: 'Avg/Month',
      sortable: false,
      numeric: true,
      render: (value) => value ? value.toLocaleString() : '0',
    },
    {
      id: 'created_at',
      label: 'Created',
      sortable: true,
      render: (value) => formatDate(value),
    },
    {
      id: 'actions',
      label: 'Actions',
      sortable: false,
      align: 'center',
      render: (_, row) => (
        <Stack direction="row" spacing={1} justifyContent="center">
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => handleViewDetails(row)}>
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEditCustomer(row)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="View API Keys">
            <IconButton size="small" onClick={() => handleViewApiKeys(row)}>
              <VpnKeyIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="View Run History">
            <IconButton size="small" onClick={() => handleViewHistory(row)}>
              <HistoryIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (error && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <PageHeader
          title="Customer Management"
          subtitle="Manage all platform customers"
        />
        <ErrorState error={error} onRetry={loadCustomers} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Customer Management"
        subtitle="Manage all platform customers"
        actions={[
          { label: 'Refresh', onClick: loadCustomers, icon: <RefreshIcon />, variant: 'outlined', disabled: loading },
        ]}
        searchValue={search}
        onSearchChange={setSearch}
        onSearch={handleSearch}
        searchPlaceholder="Search by name, tag, or ID"
        showSearch
      />

      <Alert
        severity="info"
        variant="outlined"
        sx={{ mb: 2 }}
        action={
          <Button color="inherit" size="small" onClick={() => navigate('/dashboard/admin/customer-hub')}>
            Open Customer Hub
          </Button>
        }
      >
        This page shows each customer's functions, users and API keys. Plan changes, unlimited credits, privacy settings,
        internal notes, last activity and impersonation are in the Customer Hub.
      </Alert>

      <DataTable
        columns={columns}
        data={customers}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        sortBy={sortBy}
        sortDirection={sortDir}
        onSort={handleSort}
        rowKey="id"
        emptyMessage="No customers found."
        rowsPerPageOptions={[10, 25, 50, 100]}
      />

      {/* Customer Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Customer Details</DialogTitle>
        <DialogContent>
          {loadingDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : selectedCustomer ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>Basic Information</Typography>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Stack spacing={1}>
                  <Typography><strong>ID:</strong> {selectedCustomer.id}</Typography>
                  <Typography><strong>Customer Tag:</strong> {selectedCustomer.customer_tag}</Typography>
                  <Typography><strong>Name:</strong> {selectedCustomer.name}</Typography>
                  <Typography><strong>User Type:</strong> {selectedCustomer.user_type}</Typography>
                  <Typography><strong>Active:</strong> {selectedCustomer.active ? 'Yes' : 'No'}</Typography>
                  <Typography><strong>Stripe Customer ID:</strong> {selectedCustomer.stripe_customer_id || 'Not set'}</Typography>
                  <Typography><strong>Created:</strong> {formatDate(selectedCustomer.created_at)}</Typography>
                  <Typography><strong>Updated:</strong> {formatDate(selectedCustomer.updated_at)}</Typography>
                </Stack>
              </Paper>

              {/* Usage Summary */}
              {loadingUsage ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, mb: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : usageDetail ? (
                <>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h6">Usage Summary</Typography>
                    <Button
                      size="small"
                      endIcon={<OpenInNewIcon />}
                      onClick={() => {
                        setDetailsOpen(false);
                        navigate(`/dashboard/admin/usage`);
                      }}
                    >
                      Full Usage Dashboard
                    </Button>
                  </Stack>
                  <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    {[
                      { label: 'Total Tokens', value: (usageDetail.summary.total_tokens || 0).toLocaleString() },
                      { label: 'Function Tokens', value: (usageDetail.summary.function_tokens || 0).toLocaleString() },
                      { label: 'Chain Tokens', value: (usageDetail.summary.chain_tokens || 0).toLocaleString() },
                      { label: 'Total Runs', value: (usageDetail.summary.total_runs || 0).toLocaleString() },
                      { label: 'Functions', value: String(usageDetail.summary.functions_created || 0) },
                      { label: 'Chains', value: String(usageDetail.summary.chains_created || 0) },
                    ].map((stat) => (
                      <Grid item xs={4} sm={2} key={stat.label}>
                        <Card variant="outlined">
                          <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                            <Typography variant="caption" color="text.secondary" noWrap>{stat.label}</Typography>
                            <Typography variant="subtitle2">{stat.value}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Top functions by usage */}
                  {usageDetail.functions.length > 0 && (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Top Functions by Token Usage
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Function</TableCell>
                              <TableCell align="right">Runs</TableCell>
                              <TableCell align="right">Tokens</TableCell>
                              <TableCell align="right">Success</TableCell>
                              <TableCell width={40}></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {usageDetail.functions.slice(0, 5).map((fn) => (
                              <TableRow key={fn.id} hover>
                                <TableCell>{fn.name}</TableCell>
                                <TableCell align="right">{fn.total_runs.toLocaleString()}</TableCell>
                                <TableCell align="right">{fn.total_tokens.toLocaleString()}</TableCell>
                                <TableCell align="right">{fn.success_rate}%</TableCell>
                                <TableCell>
                                  <Tooltip title="View Run History">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setDetailsOpen(false);
                                        navigate(`/dashboard/function-runs/${fn.id}?customer_id=${selectedCustomer.id}&customer_tag=${selectedCustomer.customer_tag}`);
                                      }}
                                    >
                                      <HistoryIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </>
              ) : null}

              {selectedCustomer.config && (
                <>
                  <Typography variant="h6" gutterBottom>Configuration</Typography>
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <pre style={{ margin: 0, overflow: 'auto' }}>{formatConfig(selectedCustomer.config)}</pre>
                  </Paper>
                </>
              )}

              {selectedCustomer.firebase_users && selectedCustomer.firebase_users.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom>Users ({selectedCustomer.firebase_users.length})</Typography>
                  <TableContainer component={Paper} sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Email</TableCell>
                          <TableCell>Display Name</TableCell>
                          <TableCell>Admin</TableCell>
                          <TableCell>Active</TableCell>
                          <TableCell>Last Login</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedCustomer.firebase_users.map((fbUser, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{fbUser.email}</TableCell>
                            <TableCell>{fbUser.display_name || '-'}</TableCell>
                            <TableCell>
                              <Chip label={fbUser.is_admin ? 'Yes' : 'No'} size="small" />
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={fbUser.active ? 'Active' : 'Inactive'} 
                                color={fbUser.active ? 'success' : 'error'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{formatDate(fbUser.last_login_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {selectedCustomer.api_keys && selectedCustomer.api_keys.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom>API Keys ({selectedCustomer.api_keys.length})</Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Active</TableCell>
                          <TableCell>Monthly Limit</TableCell>
                          <TableCell>Usage</TableCell>
                          <TableCell>Created</TableCell>
                          <TableCell>Last Used</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedCustomer.api_keys.map((key) => (
                          <TableRow key={key.id}>
                            <TableCell>{key.name || '-'}</TableCell>
                            <TableCell>
                              <Chip 
                                label={key.active ? 'Active' : 'Inactive'} 
                                color={key.active ? 'success' : 'error'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{key.max_tokens_monthly || 'Unlimited'}</TableCell>
                            <TableCell>{key.usage_count || 0}</TableCell>
                            <TableCell>{formatDate(key.created_at)}</TableCell>
                            <TableCell>{formatDate(key.last_used_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)}>
        <DialogTitle>Edit Customer</DialogTitle>
        <DialogContent sx={{ minWidth: 400, pt: 2 }}>
          {editCustomer && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Editing: {editCustomer.name} ({editCustomer.customer_tag})
              </Typography>
              
              <FormControl fullWidth>
                <InputLabel>User Type</InputLabel>
                <Select
                  value={editFormData.user_type}
                  label="User Type"
                  onChange={(e) => setEditFormData({ ...editFormData, user_type: e.target.value })}
                >
                  <MenuItem value="customer">Customer</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Stripe Customer ID"
                value={editFormData.stripe_customer_id || ''}
                onChange={(e) => setEditFormData({ ...editFormData, stripe_customer_id: e.target.value })}
                placeholder="cus_XXXXXXXXXXXXX"
                helperText="Stripe customer ID for billing integration"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={editFormData.active}
                    onChange={(e) => setEditFormData({ ...editFormData, active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateCustomer}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminCustomers;